/**
 * The cells of a pivot table built from report rows: the rows are grouped by the row keys and spread over the
 * column keys, each cell the sum of its rows' values, as Superset's pivot table sums them. Rows and columns
 * are sorted by their keys, A to Z.
 */
export type PivotRowKey = string[];

export type PivotSpec<Row> = {
    /** The values that name a pivot row, outermost first; `null` shows as a dash. */
    rowKey: (row: Row) => (string | null)[];
    /** The value that names a pivot column; columns sort by it. */
    columnKey: (row: Row) => string;
    value: (row: Row) => number | null;
    /** The record the row's first label names, such as the employee; the first report row of a group gives it. */
    rowHref?: (row: Row) => string | null;
};

export type PivotRow = {
    key: string;
    labels: (string | null)[];
    href: string | null;
    cells: Map<string, number>;
    total: number;
};

export type Pivot = {
    columns: string[];
    rows: PivotRow[];
    columnTotals: Map<string, number>;
    grandTotal: number;
    /** The largest cell, the full strength of the heat shading. */
    maxCell: number;
};

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });

function compareLabels(left: (string | null)[], right: (string | null)[]): number {
    for (let index = 0; index < Math.max(left.length, right.length); index++) {
        const order = collator.compare(left[index] ?? '', right[index] ?? '');
        if (order !== 0) return order;
    }
    return 0;
}

export function buildPivot<Row>(rows: Row[], spec: PivotSpec<Row>): Pivot {
    const pivotRows = new Map<string, PivotRow>();
    const columnTotals = new Map<string, number>();
    let grandTotal = 0;
    for (const row of rows) {
        const value = spec.value(row) ?? 0;
        const labels = spec.rowKey(row);
        const key = JSON.stringify(labels);
        const column = spec.columnKey(row);
        let pivotRow = pivotRows.get(key);
        if (!pivotRow) {
            pivotRow = { key, labels, href: spec.rowHref?.(row) ?? null, cells: new Map(), total: 0 };
            pivotRows.set(key, pivotRow);
        }
        pivotRow.cells.set(column, (pivotRow.cells.get(column) ?? 0) + value);
        pivotRow.total += value;
        columnTotals.set(column, (columnTotals.get(column) ?? 0) + value);
        grandTotal += value;
    }
    const sortedRows = [...pivotRows.values()].sort((left, right) => compareLabels(left.labels, right.labels));
    const maxCell = sortedRows.reduce(
        (max, row) => [...row.cells.values()].reduce((rowMax, cell) => Math.max(rowMax, Math.abs(cell)), max),
        0
    );
    return {
        columns: [...columnTotals.keys()].sort(collator.compare),
        rows: sortedRows,
        columnTotals,
        grandTotal,
        maxCell,
    };
}

/** The sum of `value` per key, keys sorted A to Z: the bars of a chart over the report rows. */
export function sumByKey<Row>(
    rows: Row[],
    key: (row: Row) => string,
    value: (row: Row) => number | null
): { key: string; value: number }[] {
    const sums = new Map<string, number>();
    for (const row of rows) {
        sums.set(key(row), (sums.get(key(row)) ?? 0) + (value(row) ?? 0));
    }
    return [...sums.entries()]
        .sort(([left], [right]) => collator.compare(left, right))
        .map(([sumKey, sum]) => ({ key: sumKey, value: sum }));
}
